import mqtt, { type MqttClient } from "mqtt";
import {
  makeEnvelope,
  parseEnvelope,
  TOPICS,
  type FleetEnvelope,
  type MqttCredentials,
} from "../types";

export type MqttHandlers = {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (err: Error) => void;
  onEnvelope?: (topic: string, env: FleetEnvelope) => void;
};

export class AriaMqttClient {
  private client: MqttClient | null = null;
  private handlers: MqttHandlers = {};

  setHandlers(handlers: MqttHandlers): void {
    this.handlers = handlers;
  }

  get connected(): boolean {
    return this.client?.connected ?? false;
  }

  async connect(creds: MqttCredentials): Promise<void> {
    await this.disconnect();

    return new Promise((resolve, reject) => {
      const client = mqtt.connect(creds.wsUrl, {
        username: creds.username,
        password: creds.password,
        protocolVersion: 5,
        reconnectPeriod: 3000,
        clean: true,
        connectTimeout: 15_000,
      });

      const onConnect = () => {
        cleanup();
        this.client = client;
        client.on("message", (topic, payload) => {
          try {
            const env = parseEnvelope(payload);
            this.handlers.onEnvelope?.(topic, env);
          } catch {
            /* ignore malformed */
          }
        });
        client.on("close", () => this.handlers.onDisconnect?.());
        client.on("error", (err) => this.handlers.onError?.(err));
        this.handlers.onConnect?.();
        void this.subscribeDefaults().then(resolve).catch(reject);
      };

      const onError = (err: Error) => {
        cleanup();
        reject(err);
      };

      const cleanup = () => {
        client.off("connect", onConnect);
        client.off("error", onError);
      };

      client.once("connect", onConnect);
      client.once("error", onError);
    });
  }

  async disconnect(): Promise<void> {
    if (!this.client) return;
    const c = this.client;
    this.client = null;
    await new Promise<void>((resolve) => {
      c.end(false, {}, () => resolve());
    });
  }

  private async subscribeDefaults(): Promise<void> {
    if (!this.client) return;
    await this.client.subscribeAsync(TOPICS.ariaAnnounce, { qos: 1 });
    await this.client.subscribeAsync(TOPICS.ariaStatusAll, { qos: 1 });
    await this.client.subscribeAsync(TOPICS.webOutAll, { qos: 1 });
  }

  async publish(topic: string, env: FleetEnvelope): Promise<void> {
    if (!this.client?.connected) {
      throw new Error("MQTT not connected");
    }
    await this.client.publishAsync(topic, JSON.stringify(env), { qos: 1 });
  }

  async publishPairRequest(
    ariaId: string,
    deviceId: string,
    code: string,
    label: string,
  ): Promise<void> {
    const env = makeEnvelope("console.pair", ariaId, {
      deviceId,
      code,
      label,
    });
    await this.publish(TOPICS.webIn(ariaId), env);
  }

  async publishChat(
    ariaId: string,
    msgId: string,
    message: string,
    deviceToken: string,
    deviceId: string,
    operatorName: string,
    targetAgentId?: string,
  ): Promise<void> {
    const env = makeEnvelope(
      "chat.message",
      ariaId,
      {
        message,
        deviceToken,
        deviceId,
        operatorId: operatorName,
        ...(targetAgentId ? { targetAgentId } : {}),
      },
      msgId,
    );
    await this.publish(TOPICS.webIn(ariaId), env);
  }

  async publishCancel(ariaId: string, msgId: string, deviceToken: string): Promise<void> {
    const env = makeEnvelope("chat.cancel", ariaId, { deviceToken }, msgId);
    await this.publish(TOPICS.webIn(ariaId), env);
  }
}

export const mqttClient = new AriaMqttClient();
